import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import styles from './bulkChangeMealDialog.module.scss'
import { useMutation, useQuery } from '@apollo/client'
import { UPDATE_DAYCARE_CHILDREN_MEAL } from '@/modules/API/meal/updateDaycareChildMeal'
import {
    GET_DAYCARE_MEAL_HABITS,
    GET_DAYCARE_MEAL_MENUS,
    GET_DAYCARE_MEAL_TEXTURES
} from '@/modules/API/meal/getMealDaycareSettings'
import { useNotification } from '@/modules/context/NotificationContext'
import { MealHabit, MealMenu, MealTexture } from '@/types/Meal'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { ErrorOutline } from '@mui/icons-material'

interface BulkChangeMealDialogProps {
    isDialogOpen: boolean
    handleCloseDialog: () => void
    selectedChildIds: number[]
    daycareId: number
    onSuccess: () => void
}

const BulkChangeMealDialog: FunctionComponent<BulkChangeMealDialogProps> = ({
    isDialogOpen,
    handleCloseDialog,
    selectedChildIds,
    daycareId,
    onSuccess
}: BulkChangeMealDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const { data: dataMealMenus } = useQuery(GET_DAYCARE_MEAL_MENUS, { variables: { daycareId } })
    const { data: dataMealTextures } = useQuery(GET_DAYCARE_MEAL_TEXTURES, { variables: { daycareId } })
    const { data: dataMealHabits } = useQuery(GET_DAYCARE_MEAL_HABITS, { variables: { daycareId } })

    const daycareMealMenus: MealMenu[] = dataMealMenus?.getDaycareMealMenus || []
    const daycareMealTextures: MealTexture[] = dataMealTextures?.getDaycareMealTextures || []
    const daycareMealHabits: MealHabit[] = dataMealHabits?.getDaycareMealHabits || []

    const [selectedMealMenu, setSelectedMealMenu] = useState<string>('')
    const [selectedMealTexture, setSelectedMealTexture] = useState<string[]>([])
    const [selectedMealHabit, setSelectedMealHabit] = useState<string[]>([])

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

    const optionsMealMenu: SelectOptionsDeprecated = [
        {
            options: [
                { name: t('noMealMenus'), value: '' },
                ...daycareMealMenus.map(
                    (mealMenu: MealMenu): SelectOptionDeprecated => ({
                        name: mealMenu.menu,
                        value: mealMenu.id.toString()
                    })
                )
            ]
        }
    ]

    const optionsMealTexture: SelectOptionsDeprecated = [
        {
            options: daycareMealTextures.map(
                (mealTexture: MealTexture): SelectOptionDeprecated => ({
                    name: mealTexture.texture,
                    value: mealTexture.id.toString()
                })
            )
        }
    ]

    const optionsMealHabit: SelectOptionsDeprecated = [
        {
            options: daycareMealHabits.map(
                (mealHabit: MealHabit): SelectOptionDeprecated => ({
                    name: mealHabit.habit,
                    value: mealHabit.id.toString()
                })
            )
        }
    ]

    const [updateDaycareChildrenMeal] = useMutation(UPDATE_DAYCARE_CHILDREN_MEAL)

    async function handleConfirm(): Promise<void> {
        setIsSubmitting(true)

        try {
            await updateDaycareChildrenMeal({
                variables: {
                    daycareId,
                    childIds: selectedChildIds,
                    menuIds: selectedMealMenu ? [parseInt(selectedMealMenu)] : [],
                    textureIds: selectedMealTexture.map((mealTexture: string): number => parseInt(mealTexture)),
                    habitIds: selectedMealHabit.map((mealHabit: string): number => parseInt(mealHabit))
                }
            })

            showNotificationSuccess(t('bulkChangeMealSuccess'))
            onSuccess()
            handleCloseDialog()
        } catch {
            showNotificationError(t('bulkChangeMealError'))
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <Dialog open={isDialogOpen} handleClose={handleCloseDialog} className={styles.Dialog}>
            <DialogHeader title={t('bulkChangeMeal')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {selectedChildIds.length} {selectedChildIds.length > 1 ? t('childsSelected') : t('childSelected')}
                </p>
                <div className={styles.Warning}>
                    <ErrorOutline className={styles.WarningIcon} />
                    <p>{t('bulkChangeMealWarning')}</p>
                </div>
                <div className={styles.SelectContainer}>
                    <SelectUnique
                        selectOptions={optionsMealMenu}
                        selectValue={selectedMealMenu}
                        setSelectValue={setSelectedMealMenu}
                        label={t('menu')}
                        className={styles.UserInput}
                    />
                    <SelectMultiple
                        selectOptions={optionsMealTexture}
                        selectValues={selectedMealTexture}
                        setSelectValues={setSelectedMealTexture}
                        label={t('textures')}
                        className={styles.UserInput}
                    />
                    <SelectMultiple
                        selectOptions={optionsMealHabit}
                        selectValues={selectedMealHabit}
                        setSelectValues={setSelectedMealHabit}
                        label={t('habits')}
                        className={styles.UserInput}
                    />
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon
                        type={'confirm'}
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className={styles.Button}
                    />
                    <ButtonTextIcon
                        type={'cancel'}
                        onClick={handleCloseDialog}
                        disabled={isSubmitting}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default BulkChangeMealDialog
